/**
 * mock.ts — Shared UI types + static display config.
 *
 * Historical note: this file held the design-prototype fixture data the shell
 * shipped with. All fixtures were removed in the 2026-07-24 de-fake pass
 * (every surface now renders real backend data or an explicit "In arrivo"
 * state). What remains is NOT mock data:
 *   - render-contract types (DashboardStat, UploadFile, the Kb* wire types)
 *   - MOCK_KB_DOMAINS — the static domain display config (label/color/icon
 *     per KB domain key), which is configuration, not fixture data.
 * The filename is kept to avoid a noisy rename across ~10 importers.
 */

export interface DashboardStat {
  label: string;
  value: string;
  delta: string;
  trend?: "up" | "down";
  warn?: boolean;
}

export type UploadFileType = "pdf" | "xlsx" | "dwg" | "other";

export interface UploadFile {
  name: string;
  type: UploadFileType;
  size: string;
  note?: string;
}

// ─── Knowledge Base types (wire/render contracts for kbApi + KB pages) ───────

export type KbDocStatus =
  | "live"
  | "pending-review"
  | "rejected"
  | "ingesting"
  | "archived"
  | "pending-manual-merge";

export interface KbDoc {
  id: string;
  filename: string;
  domain: string;
  uploader: string;
  date: string;
  chunks: number;
  status: KbDocStatus;
  /** live synthesis stage while status === "ingesting" (drives the progress UI) */
  synthesisStage?: string | null;
  /**
   * `D-59` — which progress ladder this document is on.
   *
   * Under light-only preparation the pipeline runs `pending -> chunked ->
   * embedded -> done` and never reaches `validated`, `synthesized` or `edged`.
   * A bar indexed on the seven-stage ladder therefore sits at 29% for the whole
   * of the work and then jumps to 100%, reporting three stages that will never
   * run as outstanding — the *"progress indicators belonging to the disabled KB
   * stages"* Omar's 16 Sep brief asks to remove.
   *
   * Sent by the server from the EFFECTIVE policy, never inferred here: a project
   * whose stored mode still says `full` runs light while the service policy is
   * on, and only the server knows that.
   */
  ingestionMode?: "full" | "light";
  /**
   * `D-53` — whether a synthesis exists, SEPARATELY from availability.
   * "available" | "in_progress" | "unavailable_too_large" | "failed" | "none".
   * A `live` document with `unavailable_too_large` is exactly what the revised
   * policy produces for a very long document: its original passages are
   * searchable, its synthesis pages do not exist yet (`KB-14`).
   */
  synthesisState?: string;
  generatedPageIds?: string[];
  /**
   * The tender project this document came from, as the KB recorded it at upload
   * time. A SNAPSHOT, not a live link — the two services use separate databases,
   * so the name can be stale and the id can point at a deleted project. Any
   * surface showing `projectName` must say it is a snapshot, or compare it against
   * the live name it already has.
   */
  projectId?: string | null;
  projectName?: string | null;
  projectSyncedAt?: string | null;
  /**
   * `KBR-24` — may «Controlla di nuovo» be pressed on this document?
   *
   * Both fields are the SERVER's answer, verbatim, from the same
   * `pipeline.recheck_refusal` that `POST /v1/documents/{id}/recheck` enforces.
   * Nothing here re-derives eligibility from `status`, and nothing here composes
   * its own explanation: a second copy of that rule in TypeScript is a
   * prediction that has to be kept in agreement with the server forever, which
   * is the class of defect the 2026-08-15 intake rebuild removed by deleting the
   * prediction and asking the authority instead.
   *
   * Absent on a response from an older service build, which is why the default
   * is "not available" rather than "available": hiding a control someone may use
   * is a UI bug, offering one the server will refuse is a broken product.
   */
  canRecheck?: boolean;
  /**
   * Why the re-check is unavailable, **in Italian, ready to render**.
   *
   * NOT the server's own sentence, and the distinction matters. The server sends
   * a stable `recheck_blocked_code` and an English `message` written for a
   * developer reading a 409 — the message opens with a document identifier and
   * was, briefly, shown verbatim under an Italian button. `toKbDoc` maps the CODE
   * to Italian through `recheckReasonIt`, so what lands here is what a person
   * reads. The rule still lives in exactly one place, on the server; only the
   * wording is chosen here.
   */
  recheckBlockedReason?: string | null;
  /**
   * Why this document is not live, in the words its own run recorded. `D-37`:
   * a validation that cannot finish must leave the document *"unavailable and
   * recoverable, with a clear explanation of its state"* — and a `rejected` pill
   * on its own is a state without an explanation.
   */
  lastError?: string | null;
  /**
   * `KBR-62` — what this document's most recent contradiction check actually
   * DID, as the server computed it.
   *
   * Three states, and the third is not a rounding of the second. `complete`:
   * every required comparison was made. `incomplete`: the scan stopped at the
   * comparison ceiling — neither a failure nor a clean bill. `unknown`: no run
   * ever recorded a scan, so there is nothing to claim in either direction.
   *
   * Why it exists at all: the flag behind it has been written to the run row
   * since the judge budget existed and was read NOWHERE. On the first real
   * tender, five of eight documents stopped at 200 comparisons and two of them
   * went live with an empty review queue — a short list of contradictions read
   * as «little was wrong» when it meant «we stopped looking», and no screen
   * could tell the two apart.
   */
  scan?: KbScanOutcome | null;
}

/** `KBR-62`. Mirrors `api/schemas.py::ScanOutcome` field for field. */
export interface KbScanOutcome {
  state: "complete" | "incomplete" | "unknown";
  /** Model comparisons the scan spent, and the ceiling it spent against. */
  comparisons: number | null;
  comparisonLimit: number | null;
  /**
   * Peer documents completed, and how many the scan could see and never
   * reached. On a truncated scan `documentsCompared` is deliberately 0: the
   * scan cannot say WHICH peers it got through, so it claims none of them.
   */
  documentsCompared: number | null;
  documentsUncompared: number | null;
  /** The document's own size in chunks — why a ceiling was reached. */
  chunks: number | null;
  /**
   * `HON-6` (2026-09-13) — WHY an incomplete check is incomplete. "budget": the
   * comparison ceiling was reached. "failures": comparisons were attempted and
   * did not come back, which reaches no ceiling at all. The screen printed the
   * first sentence for both, which sent an administrator looking for a budget
   * to raise that had never been exhausted.
   */
  cause?: "budget" | "failures" | null;
}

export interface KbDomain {
  key: string;
  label: string;
  color: string;
  /** key into the icon set `I` */
  icon: string;
}

/** Static domain display config (not fixture data): label/color/icon per key. */
export const MOCK_KB_DOMAINS: KbDomain[] = [
  { key: "HR", label: "HR", color: "#7C3AED", icon: "Suppliers" },
  { key: "IT", label: "IT", color: "#2563EB", icon: "Settings" },
  { key: "Procurement", label: "Procurement", color: "#B8730C", icon: "Euro" },
  { key: "Product", label: "Product", color: "#16A34A", icon: "Box" },
  { key: "Reference", label: "Reference", color: "#525866", icon: "File" },
  { key: "Altro", label: "Altro", color: "#8B5CF6", icon: "Folder" },
  { key: "Project", label: "Progetto", color: "#0EA5E9", icon: "Building" },
];

export type KbDecision = "accept_new" | "reject_new" | "merge" | "clarification";

export interface KbReview {
  id: string;
  newDocId: string;
  existingDocId: string | null;
  domain: string;
  uploader: string;
  uploadedAt: string;
  confidence: number;
  newSnippet: string;
  existingSnippet: string | null;
  existingDocSource: string | null;
  newDocSource: string;
  aiReasoning: string;
  aiRecommendation: KbDecision;
  /**
   * WHICH mechanism raised this item — `HON-10` on the server, consumed here
   * 2026-09-14.
   *
   * `deterministic` means a rule cascade decided it and no model was called;
   * `ai` means a judge call weighed it; `null` is a row filed before the column
   * existed, and is shown as neither. The server has recorded this since
   * migration 0016 and nothing read it, so every row — including the ones a
   * comparator decided by arithmetic — was presented under «Analisi AI» with a
   * confidence of «100%». A deterministic verdict writes confidence 1.0 as a
   * SENTINEL meaning "the rule is certain of its own verdict"; rendering it as a
   * measured percentage beside an AI label states two untrue things at once.
   */
  method?: "deterministic" | "ai" | null;
  decidedAt?: string;
  decidedBy?: string;
  decision?: KbDecision;
  /**
   * `KBR-62` — what the scan that RAISED this item actually did.
   *
   * An administrator reading the queue is being asked to decide on a LIST, and
   * the one thing a list cannot tell you about itself is whether it is the whole
   * list. On the first real tender three of the five truncated scans raised
   * conflicts that landed here looking exactly like conflicts a completed scan
   * had raised.
   */
  newDocScan?: KbScanOutcome | null;
}

// ─── Esplora KB — synthesized wiki pages, edges, lint ────────────────────────

export type WikiPageType = "concept" | "entity" | "source" | "analysis";

export interface KbWikiPage {
  id: string;
  title: string;
  type: WikiPageType;
  domain: string;
  chunks: number;
  sourceFileIds: string[];
  pageRefs: string[];
  status?: KbDocStatus;
}

export interface KbWikiEdge {
  from: string;
  to: string;
  /** `ref` — an explicit wikilink between two synthesized pages.
   *  `shared_source` — two pages written from the same source PDF (the honest
   *  name for what the server used to call `sim`; it never measured meaning).
   *  `shared_concept` — two pages about one recognised concept, however
   *  differently titled and whichever tenders they belong to (Phase 2). */
  type: "ref" | "shared_source" | "shared_concept";
}

export type LintCategory =
  | "contradiction"
  | "stale"
  | "orphan"
  | "missing-concept"
  | "missing-link"
  | "data-gap";

export interface KbLintFinding {
  id: string;
  category: LintCategory;
  severity: "warn" | "info";
  title: string;
  summary: string;
  suggestion: string;
  isNew?: boolean;
  pageA?: string;
  pageB?: string;
  linkedReviewId?: string;
}
